// Conversation List Component - Shows all conversations for a user
import React, { useState } from 'react';
import { MessageCircle, Search, Archive, MoreVertical, User, Clock } from 'lucide-react';
import { Conversation } from '../../types/messaging';

interface ConversationListProps {
  conversations: Conversation[];
  activeConversationId: string | null;
  userRole: 'provider' | 'client';
  onSelectConversation: (conversationId: string) => void;
  onArchiveConversation?: (conversationId: string) => void;
  loading?: boolean;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  activeConversationId,
  userRole,
  onSelectConversation,
  onArchiveConversation,
  loading = false
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  // Filter conversations by search query
  const filteredConversations = conversations.filter((conv) => {
    const searchName = userRole === 'provider' ? conv.clientName : conv.providerName;
    return searchName.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Format timestamp for display
  const formatTimestamp = (date: Date): string => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  // Get unread count for current user
  const getUnreadCount = (conv: Conversation): number => {
    return conv.unreadCount[userRole] || 0;
  };

  // Get the other participant's name
  const getOtherParticipantName = (conv: Conversation): string => {
    return userRole === 'provider' ? conv.clientName : conv.providerName;
  };

  // Truncate message preview
  const truncateMessage = (message: string, maxLength: number = 40): string => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  const handleMenuClick = (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    setMenuOpenId(menuOpenId === conversationId ? null : conversationId);
  };

  const handleArchive = (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    setMenuOpenId(null);
    onArchiveConversation?.(conversationId);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#1e293b',
        borderRight: '1px solid #334155'
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px',
          borderBottom: '1px solid #334155'
        }}
      >
        <h2
          style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#f8fafc',
            margin: '0 0 12px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <MessageCircle size={20} />
          Messages
        </h2>

        {/* Search Input */}
        <div
          style={{
            position: 'relative'
          }}
        >
          <Search
            size={16}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#64748b'
            }}
            aria-hidden="true"
          />
          <input
            type="text"
            placeholder={`Search ${userRole === 'provider' ? 'clients' : 'providers'}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search conversations"
            style={{
              width: '100%',
              padding: '10px 12px 10px 36px',
              backgroundColor: '#0f172a',
              border: '1px solid #334155',
              borderRadius: '8px',
              color: '#f8fafc',
              fontSize: '14px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>
      </div>

      {/* Conversation List */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px'
        }}
        role="list"
        aria-label="Conversations"
      >
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '40px',
              color: '#64748b'
            }}
          >
            Loading conversations...
          </div>
        ) : filteredConversations.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 20px',
              color: '#64748b',
              textAlign: 'center'
            }}
          >
            <MessageCircle size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
            <p style={{ margin: '0 0 4px 0', fontSize: '14px' }}>
              {searchQuery ? 'No conversations found' : 'No messages yet'}
            </p>
            <p style={{ margin: 0, fontSize: '12px', opacity: 0.7 }}>
              {searchQuery
                ? 'Try a different search term'
                : userRole === 'provider'
                ? 'Start a conversation with a client'
                : 'Your provider will message you here'}
            </p>
          </div>
        ) : (
          filteredConversations.map((conversation) => {
            const isActive = activeConversationId === conversation.id;
            const unreadCount = getUnreadCount(conversation);
            const participantName = getOtherParticipantName(conversation);

            return (
              <button
                key={conversation.id}
                type="button"
                onClick={() => onSelectConversation(conversation.id)}
                aria-label={`Conversation with ${participantName}${unreadCount > 0 ? `, ${unreadCount} unread messages` : ''}`}
                aria-pressed={isActive}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '12px',
                  marginBottom: '4px',
                  backgroundColor: isActive ? '#1e40af' : 'transparent',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  position: 'relative',
                  border: 'none',
                  width: '100%',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = '#334155';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {/* Avatar */}
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    backgroundColor: isActive ? '#3b82f6' : '#475569',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '12px',
                    flexShrink: 0
                  }}
                  aria-hidden="true"
                >
                  <User size={22} color="#f8fafc" />
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '4px'
                    }}
                  >
                    <span
                      style={{
                        fontWeight: unreadCount > 0 ? '600' : '500',
                        color: '#f8fafc',
                        fontSize: '14px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {participantName}
                    </span>
                    <span
                      style={{
                        fontSize: '11px',
                        color: '#94a3b8',
                        flexShrink: 0,
                        marginLeft: '8px'
                      }}
                    >
                      {conversation.lastMessage
                        ? formatTimestamp(conversation.lastMessage.timestamp)
                        : ''}
                    </span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span
                      style={{
                        fontSize: '13px',
                        color: unreadCount > 0 ? '#e2e8f0' : '#94a3b8',
                        fontWeight: unreadCount > 0 ? '500' : '400',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1
                      }}
                    >
                      {conversation.lastMessage
                        ? truncateMessage(conversation.lastMessage.content)
                        : 'No messages yet'}
                    </span>

                    {/* Unread Badge */}
                    {unreadCount > 0 && (
                      <span
                        style={{
                          backgroundColor: '#3b82f6',
                          color: '#fff',
                          fontSize: '11px',
                          fontWeight: '600',
                          padding: '2px 6px',
                          borderRadius: '10px',
                          marginLeft: '8px',
                          flexShrink: 0
                        }}
                        aria-label={`${unreadCount} unread`}
                      >
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </div>
                </div>

                {/* Menu Button */}
                {onArchiveConversation && (
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={(e) => handleMenuClick(e, conversation.id)}
                      aria-label="Conversation options"
                      aria-expanded={menuOpenId === conversation.id}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '4px',
                        cursor: 'pointer',
                        color: '#64748b',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <MoreVertical size={16} />
                    </button>

                    {/* Dropdown Menu */}
                    {menuOpenId === conversation.id && (
                      <div
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: '100%',
                          backgroundColor: '#1e293b',
                          border: '1px solid #334155',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                          zIndex: 100,
                          overflow: 'hidden',
                          minWidth: '140px'
                        }}
                        role="menu"
                      >
                        <button
                          onClick={(e) => handleArchive(e, conversation.id)}
                          role="menuitem"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            width: '100%',
                            padding: '10px 12px',
                            background: 'none',
                            border: 'none',
                            color: '#f8fafc',
                            fontSize: '13px',
                            cursor: 'pointer',
                            textAlign: 'left'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#334155';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Archive size={14} />
                          Archive
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ConversationList;
